<script lang="ts">
import type { Temporal } from 'temporal-polyfill';
import type { DatetimeFieldOptions } from '#layers/data-model';
import type { DisplayBlockProps } from '~/types/display-block';
import { supportedDateTimeFieldTypes } from '#layers/blocks/blocks/Input/InputDateTime.block.vue';
import { PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE, PRIMITIVE_TYPE_INT32, PRIMITIVE_TYPE_INT64 } from '#layers/data-model';

type DateTimeValue = Temporal.PlainDateTime | Temporal.ZonedDateTime | number | null;

interface DisplayFormattedDatetimeProps extends DisplayBlockProps<DateTimeValue>, DatetimeFieldOptions {
	dateFormat?: 'short' | 'medium' | 'long' | 'full';
	timeFormat?: 'short' | 'medium' | 'long' | 'full';
}

const __DsBlock = {
	type: 'display',
	name: '$t:blocks.display_formatted_datetime.name',
	description: '$t:blocks.display_formatted_datetime.description',
	icon: 'lucide:calendar-clock',
	category: 'display',
	hidden: true,

	fieldBinding: {
		supportedTypes: supportedDateTimeFieldTypes,
		defaultProps: ({ typeOptions }) => ({
			defaultTimezone: (typeOptions as DatetimeFieldOptions | undefined)?.defaultTimezone,
		}),
	},

	options: [
		{
			name: 'value',
		},
		{
			name: 'dateFormat',
			type: 'enum',
			values: ['short', 'medium', 'long', 'full'],
		},
		{
			name: 'timeFormat',
			type: 'enum',
			values: ['short', 'medium', 'long', 'full'],
		},
		{
			name: 'defaultTimezone',
		},
	],
}</script>

<script setup lang="ts">
import { useLocale } from '@nuxt/ui/composables';
import { computed } from 'vue';
import { isNil, isZonedDateTime, unixTimestampConverter } from '#layers/base';
import { resolveTimezone } from '#layers/base/utils/timezone';
import { defineBlock } from '#layers/block-engine';
import { usePrimitiveFormField } from '~/composables/form-block/use-primitive-form-field';
import { useTimezone } from '~/composables/use-timezone';

const props = withDefaults(defineProps<DisplayFormattedDatetimeProps>(), {
	dateFormat: 'medium',
	timeFormat: 'short',
	defaultTimezone: 'local',
});

;

const { field } = usePrimitiveFormField({});

const isTimestamp = computed(() => {
	if (field.value?.type.name) {
		return [PRIMITIVE_TYPE_INT32, PRIMITIVE_TYPE_INT64].includes(field.value.type.name);
	}

	return typeof props.value === 'number';
});

const isTimezoneType = computed(() => {
	if (field.value?.type.name) {
		return field.value.type.name === PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE;
	}

	return isZonedDateTime(props.value);
});

/**
 * Current timezone for the datetime value.
 * - For dateTimeWithTimezone: uses defaultTimezone (defaults to user's local timezone)
 * - For other types: always uses 'UTC' for consistency
 */
const currentTimezone = computed(() => {
	if (!isTimezoneType.value) {
		return 'UTC';
	}

	const fallbackTz = props.defaultTimezone?.trim() || 'local';
	return resolveTimezone(fallbackTz);
});

const dateTime = computed<Temporal.PlainDateTime | null>(() => {
	if (!props.value) return null;

	if (isTimestamp.value) {
		return unixTimestampConverter.toPlainDateTime(props.value as number, currentTimezone.value);
	}

	if (isZonedDateTime(props.value)) {
		return props.value.withTimeZone(currentTimezone.value).toPlainDateTime();
	}

	return props.value as Temporal.PlainDateTime;
});

const { code: locale } = useLocale();

const { timezoneLabel, tooltipContent } = useTimezone({
	timezone: currentTimezone,
	dateTime,
	locale,
});

const formattedDatetime = computed(() => dateTime.value?.toLocaleString(undefined, {
	dateStyle: props.dateFormat,
	timeStyle: props.timeFormat,
}));
</script>

<template>
	<DDisplayValueEmpty v-if="isNil(value)" />
	<span
		v-else
		class="inline-flex max-w-full min-w-0 items-baseline gap-1 overflow-hidden align-bottom whitespace-nowrap"
	>
		<span class="min-w-0 truncate whitespace-nowrap">
			{{ formattedDatetime }}
		</span>
		<DTooltip
			v-if="isTimezoneType && tooltipContent"
			:delay-duration="0"
			:text="tooltipContent"
		>
			<span class="flex-none whitespace-nowrap text-dimmed">{{ timezoneLabel }}</span>
		</DTooltip>
	</span>
</template>
